import { useRef, useState } from "react";

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ButtonBase from "@mui/material/ButtonBase";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { UseGetFlag } from "../../app/launchDarkly";
import useCheckHistory from "../../hooks/useCheckHistory";
import useLogRecipeEdit from "../../hooks/useLogRecipeEdit";
import { BackIconColor, UpIconBlue } from "../../iconRef/mealPLanIcons";
import { EventLogsEnum } from "../../utils/events";
import LoadingUpAnimationGeneratingMealPlan from "./LoadingUpAnimationGenratingMealPlan";
import styles from "./MealPlanWithAi.module.css";
import useMealPlanCreation from "./utils/useMealPlanCreation";
import { setSelectedDate } from "../../app/mealPlan/mealPlan.slice";
import { useAppDispatch } from "../../app/store/hooks";

const MealPlanWithAi = () => {
  const goBackOrRedirect = useCheckHistory();
  const { mealPlanQuickOptions } = UseGetFlag();
  const { generateMealPlan, loading: generatingMealPLan } = useMealPlanCreation();

  const [days, setDays] = useState<number>(7);
  const [showPrompts, setShowPrompts] = useState<boolean>(mealPlanQuickOptions?.length);
  const [inputValue, setInputValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { logRecipeEdit } = useLogRecipeEdit();
  const dispatch = useAppDispatch()
  const handleBackClick = () => {
    goBackOrRedirect('/meal-plan');
  }

  const incrementDays = () => {
    setDays((prevDays) => (prevDays < 30 ? prevDays + 1 : prevDays));
  };

  const decrementDays = () => {
    setDays((prevDays) => (prevDays > 1 ? prevDays - 1 : 1));
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
    setShowPrompts(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (input: string) => {
    setInputValue(input);
    if (showPrompts) setShowPrompts(false);
    if (input.trim() === '') setShowPrompts(true);
  }

  const handleMealPlanCreation = () => {
    logRecipeEdit(EventLogsEnum.GENERATE_MEAL_PLAN, {
      eventData: {
        prompt: inputValue,
        days
      }
    })
    generateMealPlan(inputValue, days);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1); 
    dispatch(setSelectedDate(tomorrow));
  }

  if (generatingMealPLan) return <LoadingUpAnimationGeneratingMealPlan />

  return (
    <Grid className={styles.main_container}>
      <Grid className={styles.header_icons}><BackIconColor onClick={handleBackClick} /></Grid>
      <Grid className={styles.header_wrapper}><UpIconBlue /><Typography className={styles.header_text} variant='headerOutfit'>Personalise your meal with AI</Typography></Grid>

      <Grid className={styles.days_section}>
        <Typography className={styles.days_text} variant="tagsAndTimeAndServingSize">No. of days</Typography>
        <Grid className={styles.days_container}>
          <Typography variant="caption4LightOutfit">{days} {days === 1 ? "Day" : "Days"}</Typography>
          <Grid className={styles.days_controller}>
            <ButtonBase className={styles.controller_icon_container} onClick={decrementDays}><RemoveIcon className={styles.controller_icon} /></ButtonBase>
            <ButtonBase className={styles.controller_icon_container} onClick={incrementDays}><AddIcon className={styles.controller_icon} /></ButtonBase>
          </Grid>
        </Grid>
      </Grid>

      <Grid className={styles.prompt_container}>
        <TextField
          className={styles.text_area}
          fullWidth
          inputRef={inputRef}
          multiline
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="Your message..."
          rows={5}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              position: "relative",
              "& fieldset": {
                border: "none"
              },
              "&:hover fieldset": {
                border: "none"
              },
              "&.Mui-focused": {
                "&:before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "12px",
                  padding: "1px",
                  background: "linear-gradient(99.24deg, #5219F9 -6.88%, #FF8A00 171.12%)",
                  mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  maskComposite: "exclude",
                  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "destination-out",
                  pointerEvents: "none"
                }
              }
            },
            "& .MuiOutlinedInput-input": {
              fontFamily: "Outfit",
              fontSize: "14px",
              color: "#0E204D",
              lineHeight: "1.4",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#0E204DB2",
            },
          }}
          value={inputValue}
          variant="outlined"
        />
      </Grid>

      {showPrompts ? (
        <Grid className={styles.preset_prompts_section}>
          {mealPlanQuickOptions?.length && mealPlanQuickOptions.map((prompt: any) => (
            <Grid className={styles.prompt} key={prompt.key} onClick={() => handlePromptClick(prompt.value)}>
              <Typography className={styles.prompt_text} variant="caption4Light">{prompt.value}</Typography>
              <ButtonBase className={styles.prompt_add_button}><AddIcon className={styles.prompt_add_icon} /></ButtonBase>
            </Grid>
          ))}
        </Grid>
      ) : (
        <ButtonBase className={styles.generate_meal_button} onClick={handleMealPlanCreation}>
          <Typography variant="ingredientText2miniOutfit">Plan my meal</Typography>
        </ButtonBase>
      )}
    </Grid>
  )
}

export default MealPlanWithAi;
